import React, { useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Stack,
  Collapse,
  PopoverArrow,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useColorModeValue,
  useDisclosure,
  StackDivider,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { useStaticQuery, graphql, withPrefix } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import { Logo } from '../Logo';
import { Link } from '../Link';
import { DownloadLinks } from '../DownloadLinks';

export default function Navigation() {
  const { isOpen, onToggle } = useDisclosure();
  const currentLocation = useLocation();

  useEffect(() => {
    if (isOpen) {
      document.getElementsByTagName('html')[0].style.overflow = 'hidden';
    } else {
      document.getElementsByTagName('html')[0].style.overflow = 'auto';
    }
  }, [isOpen]);

  return (
    <Box as='nav' role='navigation'>
      <Flex alignItems='center' px={{ base: 15, xl: 30 }} py={{ base: 10, xl: 0 }}>
        <Flex flex={{ base: 1 }} justify='start' alignItems='stretch'>
          <Link
            to='/'
            _hover={{
              textDecoration: 'none',
              opacity: 0.8,
            }}
            py={{ base: 5, xl: 10 }}
          >
            <Logo />
          </Link>

          <Flex display={{ base: 'none', xl: 'flex' }} ml={20} alignItems='stretch'>
            <DesktopNav currentLocation={currentLocation} />
          </Flex>
        </Flex>
        <Flex display={{ base: 'flex', xl: 'none' }}>
          <IconButton
            onClick={onToggle}
            icon={isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />}
            variant='transparent'
            aria-label='Toggle Navigation'
            aria-expanded={isOpen}
          />
        </Flex>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <MobileNav currentLocation={currentLocation} />
      </Collapse>
    </Box>
  );
}

const url = (item) => {
  const { internal, external } = item || {};
  if (internal) {
    if (internal.section === 'single') {
      // Single category pages have URLs at the root of the site
      return `${withPrefix('/')}${internal.slug.current}/`;
    }
    return `${withPrefix('/')}${internal.section}/${internal.slug.current}/`;
  }
  if (external) {
    return external;
  }

  return null;
};

const isActive = (navItem, currentLocation) => {
  if (currentLocation.pathname === url(navItem.url)) {
    return true;
  }

  if (navItem.children) {
    const activeChild = navItem.children.find(
      (child) => currentLocation.pathname === url(child.url),
    );
    if (activeChild) {
      return true;
    }
  }

  const downloadPaths = [
    'french',
    'english',
    'spanish',
    'portuguese',
    'arabic',
    'russian',
    'chinese',
    'japanese',
    'german',
  ];

  if (
    navItem.text === 'Downloads' &&
    downloadPaths.includes(
      currentLocation.pathname.split('/')[currentLocation.pathname.split('/').length - 2],
    )
  ) {
    return true;
  }

  return false;
};

const DesktopNav = ({ currentLocation }) => {
  const data = useStaticQuery(NAVIGATION_QUERY);
  const { items } = data.data;
  const popoverContentBgColor = useColorModeValue('white', 'gray.800');

  return (
    <Stack direction='row' spacing={4} alignItems='stretch'>
      {items.map((navItem) => (
        <Box key={navItem.text} height='100%'>
          <Popover trigger='hover' placement='bottom-start'>
            <PopoverTrigger>
              <Box
                as={navItem.url ? Link : 'button'}
                py={30}
                px={20}
                to={navItem.url}
                textStyle='navItem'
                color={isActive(navItem, currentLocation) ? 'black' : 'white'}
                bg={isActive(navItem, currentLocation) ? 'white' : 'transparent'}
                height='100%'
                display='flex'
                alignItems='center'
                letterSpacing='1px'
                maxWidth='150px'
                _hover={{
                  textDecoration: 'none',
                  bg: isActive(navItem, currentLocation) ? 'lightBlue' : 'white',
                  color: 'black',
                }}
              >
                {navItem.text}
              </Box>
            </PopoverTrigger>

            {navItem.children.length ? (
              <PopoverContent
                boxShadow='lg'
                border='1px solid rgba(0,0,0,0.15)'
                bg={popoverContentBgColor}
                p={4}
                rounded='xl'
                minW='sm'
                color='black'
              >
                <PopoverArrow bg={popoverContentBgColor} />
                <Stack className='nav nav-desktop'>
                  {navItem.text === 'Downloads' && (
                    <Box>
                      <Text
                        fontWeight='600'
                        padding='8px'
                        textTransform='uppercase'
                        fontSize='0.875rem'
                        letterSpacing='0.05em'
                      >
                        Download Full Report
                      </Text>
                      <DownloadLinks
                        paddingLeft='8px'
                        display='grid'
                        gridTemplateColumns='1fr'
                        variant='header'
                      />
                    </Box>
                  )}
                  {navItem.children.map((child) => (
                    <DesktopSubNav key={child.text} currentLocation={currentLocation} {...child} />
                  ))}
                </Stack>
              </PopoverContent>
            ) : null}
          </Popover>
        </Box>
      ))}
    </Stack>
  );
};

const DesktopSubNav = ({ text, url, file }) => (
  <Link
    to={url}
    href={file?.asset?.url && file.asset.url}
    target={file?.asset?.url && '_blank'}
    role='group'
    variant='header'
  >
    <Stack direction='row' align='center'>
      <Box>
        <Text transition='all .3s ease'>{text}</Text>
      </Box>
    </Stack>
  </Link>
);

const MobileNav = () => {
  const data = useStaticQuery(NAVIGATION_QUERY);
  const { items } = data.data;
  return (
    <Stack
      bg='white'
      pt={30}
      pb='12.5rem'
      px={20}
      display={{ xl: 'none' }}
      spacing={20}
      divider={<StackDivider borderColor='gray.200' />}
      color='black'
      height='100vh'
      overflowY='scroll'
      className='nav'
    >
      {items.map((navItem) => (
        <MobileNavItem key={navItem.text} {...navItem} />
      ))}
    </Stack>
  );
};

const MobileNavItem = ({ text, url, children }) => (
  <Stack align='center' spacing={0}>
    {children.length ? (
      <Text fontWeight={600} textStyle='h3'>
        {text}
      </Text>
    ) : (
      <Text fontWeight={600} textStyle='h3'>
        Introduction
      </Text>
    )}
    {text === 'Downloads' && (
      <Box>
        <Text
          fontWeight='600'
          padding='8px'
          textTransform='uppercase'
          fontSize='0.875rem'
          letterSpacing='0.05em'
        >
          Download Full Report
        </Text>
        <DownloadLinks
          textAlign='center'
          display='grid'
          gridTemplateColumns='1fr'
          variant='header'
        />
      </Box>
    )}
    {children.length ? (
      children.map((child) => (
        <Link
          key={child.text}
          py={2}
          to={child.url}
          href={child.file?.asset?.url && child.file.asset.url}
          color='black'
          fontSize='lg'
          textAlign='center'
        >
          {child.text}
        </Link>
      ))
    ) : (
      <Link py={2} to={url} color='black' fontSize='lg' textAlign='center'>
        {text}
      </Link>
    )}
  </Stack>
);

const NAVIGATION_QUERY = graphql`
  query Navigation {
    data: sanityMeta {
      items: navItems {
        url: navItemUrl {
          internal: internalLink {
            slug {
              current
            }
            section
          }
          external: externalUrl
        }
        children {
          url: navItemUrl {
            external: externalUrl
            internal: internalLink {
              slug {
                current
              }
              section
            }
          }
          text
          file {
            asset {
              url
            }
          }
        }
        text
        file {
          asset {
            url
          }
        }
      }
    }
  }
`;
